/**
 * responsiveVisibility — single source of truth for the per-block responsive
 * visibility primitive (`hiddenOn`).
 *
 * A block placement carries `hiddenOn: string[]` — the device bands on which it
 * must be hidden. On the PUBLISHED page this is applied as real responsive CSS
 * (container queries, see `index.css`) so it works for actual visitors with no
 * JS — keyed off the `data-builder-hide` attribute this module emits. The query
 * container is the canvas `<main id="builder-canvas">` (declared `@container`),
 * which is width-constrained in the builder preview and spans the viewport when
 * published — so ONE class set drives both contexts.
 *
 * Device → width band (MUST stay in sync with the `@container` rules in
 * `index.css`):
 *   - mobile  : < 768px
 *   - tablet  : 768px – 1023px
 *   - desktop : ≥ 1024px
 *
 * RÈGLE QUI EN DÉCOULE, et qui vaut pour TOUT le responsive d'un bloc : un bloc
 * ne déclare JAMAIS de point de rupture d'ÉCRAN (`md:`, `lg:` = `@media`), qui
 * interroge la fenêtre — laquelle ne bouge pas quand on change d'appareil dans
 * l'aperçu. Il déclare des requêtes de CONTENEUR : `@2xl:` (672), `@3xl:` (768),
 * `@5xl:` (1024), `@7xl:` (1280) — les équivalents de `sm`, `md`, `lg`, `xl`.
 * Piège de rédaction : `@` est un indicateur RÉSERVÉ en YAML, donc une classe qui
 * COMMENCE par une requête de conteneur doit être citée (`class: '@5xl:hidden …'`)
 * — sans quoi la déf ne parse plus et le bloc rend « Unknown block type ».
 * Le garde-fou déterministe des deux règles :
 * `__tests__/blockSeedsResponsive.test.js`.
 *
 * The mechanism is deliberately CSS-attribute-driven (not a utility-class
 * variant): the project runs two CSS engines — Tailwind v4 at build time and
 * UnoCSS at runtime — and block classes are generated at runtime from data, so
 * hand-written CSS keyed off a data attribute is the robust, engine-independent
 * choice (mirrors how `editable:` → `data-builder-field` already works).
 */

export const RBOX_CLASS = 'builder-rbox'
export const HIDE_ATTR = 'data-builder-hide'

/** The device bands the visibility toggle understands. */
export const RESPONSIVE_DEVICES = ['mobile', 'tablet', 'desktop']

/**
 * Les mêmes appareils en largeurs CONCRÈTES, pour un aperçu rendu HORS du
 * canevas (catalogue de blocs). Le canevas peut laisser « desktop » s'étirer à
 * 100 % (`DEVICE_WIDTHS`) ; un aperçu doit fixer un nombre, parce que c'est lui
 * qui fabrique le conteneur de requêtes contre lequel le bloc se mesure — sans
 * conteneur, TOUT bloc retombe sur ses styles de base, c'est-à-dire sa version
 * mobile.
 *
 * Les valeurs vivent ici, à côté des bandes qu'elles doivent respecter (voir
 * l'en-tête du fichier et les règles `@container` d'index.css) : mobile < 768,
 * tablette 768–1023, desktop ≥ 1024. Les déplacer sans y toucher ferait mentir
 * l'aperçu.
 */
export const DEVICE_PREVIEW_WIDTHS = { mobile: 375, tablet: 768, desktop: 1280 }

/**
 * Les bandes en LARGEUR MAXIMALE, pour tout ce qui doit écrire une condition
 * (`sizes`, `<source media>` — voir `imageSizing.js`). Le desktop n'y est pas :
 * c'est le défaut, la bande sans condition. Elles sont ici, et pas dans le
 * module qui les consomme, parce que ce fichier se déclare source unique des
 * bandes ; les `@container` d'index.css doivent leur rester d'accord.
 */
export const BAND_MAX_WIDTHS = { mobile: 767.98, tablet: 1023.98 }

/**
 * Build the `data-builder-hide` attribute value from a `hiddenOn` list.
 * Returns a space-separated device list (matched by the `[data-builder-hide~=…]`
 * CSS selectors), or `undefined` when nothing is hidden — so the attribute is
 * omitted entirely and the element stays a plain `display:contents` wrapper.
 *
 * @param {string[]} hiddenOn
 * @returns {string|undefined}
 */
export const hideAttrValue = (hiddenOn) => {
  if (!Array.isArray(hiddenOn) || hiddenOn.length === 0) return undefined
  const value = hiddenOn.filter((d) => RESPONSIVE_DEVICES.includes(d)).join(' ')
  return value || undefined
}
